// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable formatjs/no-literal-string-in-jsx */
import { PageLayout } from "#/features/global-ui/page-layout"
import { PageNotFound } from "#/components/views/error/PageNotFound"
import { Title } from "#/services/page-title/Title"
import { Paths } from "#/utils/Paths"
import { Alert, Box, Progress, Typography } from "@carrotfertility/carotene-core"
import { IntakeForm, PhotoIdUpload, useTelemedicineAccess } from "@internal/telemedicine"
import { TRPCClientError } from "@trpc/client"
import React from "react"
import { Redirect, useParams } from "react-router-dom"
import { useIntakeForm } from "./hooks/useIntakeForm"
import { usePhotoIdOnFile } from "./hooks/usePhotoIdOnFile"
import { useSubmitIntakeForm } from "./hooks/useSubmitIntakeForm"
import { useUploadIdDocuments } from "./hooks/useUploadIdDocuments"

const errorMessage = (e: unknown): string => (e instanceof Error ? e.message : "unknown error")

function isNotFoundError(error: unknown): boolean {
  if (!(error instanceof TRPCClientError)) return false
  const data = (error as TRPCClientError<never>).data as { code?: string } | undefined
  return data?.code === "NOT_FOUND"
}

const CenteredProgress = () => (
  <Box
    sx={(theme) => ({
      marginBlockStart: theme.tokens.spacing.md,
      display: "flex",
      justifyContent: "center",
      alignItems: "center"
    })}
  >
    <Progress />
  </Box>
)

export function TelemedicineBookingFormsPage() {
  const { id } = useParams<{ id: string }>()
  const { data: access, isLoading, isError } = useTelemedicineAccess()
  const hasAccess = access != null && access.eligible && access.entry.status !== "single_unqualified"

  const intakeForm = useIntakeForm(id, hasAccess)
  const photoIdOnFile = usePhotoIdOnFile(hasAccess)
  const submit = useSubmitIntakeForm()
  const uploadIds = useUploadIdDocuments()

  // A failed access read is anomalous at this entry point — send the member home rather than
  // render the PoC page.
  if (isError) {
    return <Redirect to={Paths.HOME} />
  }

  if (isLoading || !access) {
    return null
  }

  if (!hasAccess) {
    return <Redirect to={Paths.HOME} />
  }

  // A NOT_FOUND means the appointment isn't this member's (ownership enforced in getIntakeForm)
  if (intakeForm.error && isNotFoundError(intakeForm.error)) {
    return <PageNotFound />
  }

  const renderQuestionnaire = (): JSX.Element => {
    const form = intakeForm.data?.form
    if (!form) return <Typography>No intake form is connected to this appointment.</Typography>
    return (
      <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.md })}>
        <IntakeForm
          key={form.id}
          form={form}
          onSubmit={async (answers) => {
            await submit.mutateAsync({ formId: form.id, answers })
          }}
        />
        {submit.isSuccess ? <Alert severity="success">Submitted — form answer group #{submit.data.id}</Alert> : null}
        {submit.error ? <Alert severity="error">Submit failed: {errorMessage(submit.error)}</Alert> : null}
      </Box>
    )
  }

  const renderIdStep = (): JSX.Element => {
    if (photoIdOnFile.isLoading) return <CenteredProgress />
    const idComplete = photoIdOnFile.data?.onFile === true || uploadIds.isSuccess
    if (!idComplete) {
      return (
        <>
          <PhotoIdUpload
            onSubmit={async (images) => {
              await uploadIds.mutateAsync(images)
            }}
            submitting={uploadIds.isPending}
          />
          {uploadIds.error ? <Alert severity="error">ID upload failed: {errorMessage(uploadIds.error)}</Alert> : null}
        </>
      )
    }
    return renderQuestionnaire()
  }

  const renderContent = (): JSX.Element => {
    if (intakeForm.isLoading) return <CenteredProgress />

    if (intakeForm.error) {
      return <Alert severity="error">Something went wrong loading these questions. Please try again.</Alert>
    }

    return renderIdStep()
  }

  return (
    <PageLayout variant="sidebar">
      <Title title="Telemedicine Intake Form" />
      {renderContent()}
    </PageLayout>
  )
}
